"use client";

import Image from "next/image";
import { useEffect, useState, type ReactNode } from "react";
import { Play } from "lucide-react";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { cn } from "~/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./dialog";
import { VideoPlayer } from "./video-player";

const DEFAULT_VIDEO_ASPECT_RATIO = 9 / 16;

interface VideoIntroductionDialogProps {
  videoUrl: string;
  therapistName?: string;
  therapistImage?: string | null;
  avatarClassName?: string;
  playButtonClassName?: string;
  triggerClassName?: string;
  children?: ReactNode;
  autoPlay?: boolean;
  avatarSize?: "small" | "large";
}

export function VideoIntroductionDialog({
  videoUrl,
  therapistName,
  therapistImage,
  avatarClassName,
  playButtonClassName,
  triggerClassName,
  children,
  autoPlay = true,
  avatarSize = "large",
}: VideoIntroductionDialogProps) {
  const [isHydrated, setIsHydrated] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [aspectRatio, setAspectRatio] = useState(DEFAULT_VIDEO_ASPECT_RATIO);
  const title = `Video predstavljanje${therapistName ? ` - ${therapistName}` : ""}`;

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    setAspectRatio(DEFAULT_VIDEO_ASPECT_RATIO);
  };

  const trigger = children ?? (
    <button
      className={cn(
        "group relative touch-manipulation rounded-full transition-transform duration-200",
        "focus:ring-primary focus:ring-2 focus:ring-offset-2 focus:outline-hidden",
        "active:scale-95 md:active:scale-100",
        triggerClassName,
      )}
      aria-label="Pogledaj video predstavljanje"
    >
      <div
        className={cn(
          "group-hover:ring-primary/20 relative shrink-0 overflow-hidden rounded-full ring-2 ring-transparent ring-offset-2 transition-shadow duration-200 group-hover:shadow-lg",
          avatarClassName ??
            (avatarSize === "small" ? "h-16 w-16" : "h-40 w-40"),
        )}
      >
        {therapistImage ? (
          <Image
            src={therapistImage}
            alt={therapistName || "Profilna slika terapeuta"}
            width={avatarSize === "small" ? 64 : 320}
            height={avatarSize === "small" ? 64 : 320}
            sizes={avatarSize === "small" ? "64px" : "320px"}
            className="h-full w-full object-cover"
          />
        ) : (
          <span className="from-primary/10 to-primary/5 flex h-full w-full items-center justify-center bg-linear-to-br text-sm">
            {(therapistName || "T")[0]}
          </span>
        )}
      </div>
      {avatarSize === "large" && (
        <span
          className={cn(
            "absolute inset-0 flex items-center justify-center rounded-full bg-black/30 backdrop-blur-[1px]",
            playButtonClassName,
          )}
        >
          <span className="bg-background/90 flex h-16 w-16 items-center justify-center rounded-full shadow-lg">
            <Play className="text-primary ml-1 h-7 w-7" />
          </span>
        </span>
      )}
    </button>
  );

  if (!isHydrated) {
    return trigger;
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        aria-describedby={undefined}
        className="w-[calc(100%-2rem)] overflow-hidden border-0 bg-black p-0 shadow-2xl"
        style={{
          maxWidth: `min(calc(100vw - 2rem), calc((100vh - 6rem) * ${aspectRatio}))`,
        }}
      >
        <VisuallyHidden>
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
        </VisuallyHidden>
        {isOpen && (
          <VideoPlayer
            src={videoUrl}
            title={title}
            autoPlay={autoPlay}
            aspectRatio={aspectRatio}
            onAspectRatioChange={setAspectRatio}
            poster={therapistImage ?? undefined}
            posterAlt={
              therapistName ? `Fotografija terapeuta ${therapistName}` : ""
            }
            contextLabel={
              therapistName
                ? `Video predstavljanje, ${therapistName}`
                : "Video predstavljanje"
            }
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
